import { Form, Input, Modal, Select } from "antd";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCreateUser, useOrganizations } from "../../api";
import { useDataConsumers } from "../../api/api.data-consumers";

interface CreateUserModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

export function CreateUserModal({ isOpen, setIsOpen }: CreateUserModalProps) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [form] = Form.useForm();

  const createUser = useCreateUser();
  const organizations = useOrganizations();
  const dataConsumers = useDataConsumers();

  const selectOptionsOrganization = useMemo(() => {
    const options =
      organizations.value?.items.map((org) => ({
        label: org.name,
        value: org.id,
        id: org.id,
      })) || [];
    // Add the CM1 organization to the options list which is used when creating new SO users.
    return [...options, { label: "CM1", value: "91207", id: "91207" }].sort(
      (a, b) => a.label.localeCompare(b.label)
    );
  }, [organizations.value]);

  const selectOptionsDataConsumer = useMemo(() => {
    const options =
      dataConsumers.value?.items.map((dc) => ({
        label: dc.name,
        value: dc.id,
        id: dc.id,
      })) || [];
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [dataConsumers.value]);

  const success = () => {
    Modal.success({
      title: t("user:create-user-success-title"),
      content: t("user:create-user-success-body"),
      onOk() {
        setIsOpen(false);
        form.resetFields();
      },
    });
  };

  const error = () => {
    Modal.error({
      title: t("error"),
      content: t("user:create-user-failed-body"),
    });
  };

  const formValid = async () => {
    try {
      await form.validateFields();
      return true;
    } catch (error) {
      return false;
    }
  };

  const validateSelections = async (
    _: unknown,
    value: string
  ): Promise<void> => {
    const currentOrgId = form.getFieldValue("orgId");
    const currentConsumerId = form.getFieldValue("consumerId");

    if (!currentOrgId && !currentConsumerId) {
      //todo: i18n
      setErrorMessage(
        "At least one Organization or Data Consumer must be selected"
      );
      return Promise.reject(new Error(undefined));
    }
    setErrorMessage(null);
    return Promise.resolve();
  };

  const handleOk = async () => {
    if (!(await formValid())) return;

    const body = {
      email: form.getFieldValue("email"),
      orgId: form.getFieldValue("orgId") ?? null,
      consumerId: form.getFieldValue("consumerId") ?? null,
    };

    setIsLoading(true);
    const result = await createUser(body);
    setIsLoading(false);

    if (result.error) error();
    else success();
  };

  const handleCancel = () => {
    form.resetFields();
    setIsOpen(false);
  };

  return (
    <>
      <Modal
        title={t("user:create-user")}
        open={isOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        confirmLoading={isLoading}
        okText={t("translations:save")}
        cancelText={t("translations:cancel")}
        aria-label="create-user-modal"
        destroyOnClose
      >
        <Form
          form={form}
          name="createUser"
          layout="vertical"
          requiredMark={false}
          style={{ maxWidth: 400, margin: "0 auto" }}
          aria-label="create-user-form"
        >
          <Form.Item
            label={t("user:email")}
            name="email"
            rules={[{ required: true, type: "email", message: t("required") }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label={t("organization")}
            name="orgId"
            help={null}
            validateStatus=""
            rules={[{ validator: validateSelections }]}
          >
            <Select allowClear options={selectOptionsOrganization}></Select>
          </Form.Item>
          <Form.Item
            label={t("integrations:data-consumer-name")}
            name="consumerId"
            help={null}
            validateStatus=""
            rules={[{ validator: validateSelections }]}
          >
            <Select allowClear options={selectOptionsDataConsumer}></Select>
          </Form.Item>
          <span style={{ color: "red" }}> {errorMessage} </span>
        </Form>
      </Modal>
    </>
  );
}
